import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { fotoNoTamanho } from "../dados/foto";

type Props = {
  candidatas: (string | null | undefined)[];
  largura: number;
  alt?: string;
  className?: string;
  style?: CSSProperties;
  reserva?: ReactNode;
  prioridade?: boolean;
};

const confirmadas = new Map<string, Promise<boolean>>();

/**
 * O Google responde 404 com um PNG de "atenção" para foto apagada, e o <img>
 * mostra esse PNG como se fosse a foto. Como lh3 libera CORS, conferimos o
 * status antes de exibir. Só o 404 descarta a foto: um 429 (limite de
 * requisições) ou falha de rede não pode apagar todas as imagens da página —
 * nesses casos deixamos o <img> tentar.
 */
const existe = (url: string): Promise<boolean> => {
  const pendente = confirmadas.get(url);
  if (pendente) return pendente;
  const promessa = fetch(url, { mode: "cors", credentials: "omit" })
    .then((resposta) => resposta.status !== 404)
    .catch(() => true);
  confirmadas.set(url, promessa);
  return promessa;
};

/**
 * Tenta as URLs na ordem e pula as que o Google já apagou (várias fotos
 * guardadas no banco devolvem 404). Sem nenhuma válida, mostra a reserva.
 */
export const Imagem = ({ candidatas, largura, alt = "", className, style, reserva = null, prioridade = false }: Props) => {
  const validas = candidatas.filter((url): url is string => !!url).map((url) => fotoNoTamanho(url, largura));
  const chave = validas.join("|");
  const [escolhida, setEscolhida] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    let ativo = true;
    setEscolhida(undefined);
    (async () => {
      for (const url of validas) {
        if (await existe(url)) {
          if (ativo) setEscolhida(url);
          return;
        }
      }
      if (ativo) setEscolhida(null);
    })();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave]);

  if (escolhida === undefined && validas.length > 0) {
    return <div className={className} style={{ ...style, background: "transparent" }} aria-hidden="true" />;
  }
  if (!escolhida) return <>{reserva}</>;

  return (
    <img
      key={escolhida}
      src={escolhida}
      alt={alt}
      className={className}
      style={style}
      loading={prioridade ? "eager" : "lazy"}
      fetchPriority={prioridade ? "high" : "auto"}
      decoding="async"
      onError={() => setEscolhida(null)}
    />
  );
};
